import { useState, useEffect } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";

// Session hook - compatible with better-auth API for easier migration
export function useSession(): {
	data: Session | null;
	isPending: boolean;
	error: Error | null;
} {
	const [session, setSession] = useState<Session | null>(null);
	const [isPending, setIsPending] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		// Get initial session
		supabase.auth
			.getSession()
			.then(({ data: { session }, error }) => {
				setSession(session);
				if (error) setError(error);
				setIsPending(false);
			})
			.catch((err) => {
				setError(err);
				setIsPending(false);
			});

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setSession(session);
			// Clean up bare # left after Supabase processes OAuth tokens
			if (window.location.hash === "" || window.location.hash === "#") {
				history.replaceState(
					null,
					"",
					window.location.pathname + window.location.search,
				);
			}
		});

		return () => subscription.unsubscribe();
	}, []);

	return { data: session, isPending, error };
}

// Convenience hook for just the user
export function useUser(): { user: User | null; isPending: boolean } {
	const { data: session, isPending } = useSession();
	return { user: session?.user ?? null, isPending };
}

// Auth actions
export async function signInWithEmail(email: string, password: string) {
	const { data, error } = await supabase.auth.signInWithPassword({
		email,
		password,
	});
	return { data, error };
}

export async function signUpWithEmail(
	email: string,
	password: string,
	metadata?: { name?: string },
) {
	const { data, error } = await supabase.auth.signUp({
		email,
		password,
		options: {
			data: metadata,
		},
	});
	return { data, error };
}

export async function signInWithGoogle(redirectPath?: string) {
	const redirectTo = redirectPath
		? new URL(redirectPath, window.location.origin).toString()
		: window.location.origin;
	const { data, error } = await supabase.auth.signInWithOAuth({
		provider: "google",
		options: {
			redirectTo,
		},
	});
	return { data, error };
}

export async function signOut() {
	// Best-effort server-side revoke. If the access token is already dead
	// the call 401s — expected, not a reason to leave the user stranded
	// in a broken-session state. Always clear local storage and redirect,
	// regardless of whether the server call succeeds.
	try {
		await supabase.auth.signOut({ scope: "global" });
	} catch {
		// ignored — local cleanup below is what actually matters to the user
	}
	await supabase.auth.signOut({ scope: "local" });
	window.location.href = "/";
	return { error: null };
}

// Legacy-compatible exports for easier migration
export const signIn = {
	email: ({ email, password }: { email: string; password: string }) =>
		signInWithEmail(email, password),
	google: (redirectPath?: string) => signInWithGoogle(redirectPath),
};

export const signUp = {
	email: ({
		email,
		password,
		name,
	}: {
		email: string;
		password: string;
		name?: string;
	}) => signUpWithEmail(email, password, { name }),
};
